/**
 * Block 12 PR-B Phase B3 Step 9 — AnketaForm pure selectors.
 *
 * Только базовые derived values от state без external dependencies (methodology,
 * formulas). Composite selectors с methodology + formulas — в `helpers/`
 * (computeMaterialRows Step 13, computeAutoServiceQty + computeTotal Step 14).
 *
 * Все функции pure + idempotent — safe для memoization в Container.
 */

import type { AnketaParams, AnketaState, FieldError, ValidationState } from '../types';

export function selectValidation(state: AnketaState): ValidationState {
  return state.validation;
}

export function selectIsValid(state: AnketaState): boolean {
  return state.validation.isValid;
}

export function selectIsDirty(state: AnketaState): boolean {
  return state.isDirty;
}

export function selectParams(state: AnketaState): AnketaParams {
  return state.params;
}

export function selectProjectName(state: AnketaState): string {
  return state.projectName;
}

/**
 * Первая ошибка для поля параметра. Используется `<SectionParams>` для inline
 * рендера сообщения под NumberInput / RadioCard (ADR-0020 §Q7 + P-022 aria-describedby).
 *
 * Path matching exact — для Kitchen `'height'`, `'length2'`, `'countertopSheetLen'` etc.
 */
export function selectErrorForPath(state: AnketaState, path: string): string | undefined {
  return state.validation.errors.find((e) => e.path === path)?.message;
}

/** Все ошибки для поля (на случай если schema выдаёт несколько issues per path). */
export function selectAllErrorsForPath(state: AnketaState, path: string): FieldError[] {
  return state.validation.errors.filter((e) => e.path === path);
}

/**
 * Сумма поля `cost` всех custom rows. Поле `cost` заполняется caller'ом при
 * `ADD_CUSTOM_ROW` / `UPDATE_CUSTOM_ROW` (Step 14 helpers computeTotal / Step 22
 * SectionServices); reducer не пересчитывает cost — здесь read-only sum.
 *
 * Hotfix-2B Phase B3 Step 5 — фильтрация по `enabled` flag: rows с
 * `enabled === false` (per-row toggle off) исключаются из суммы. Undefined →
 * trust as enabled (backward-compat for pre-2B records без поля).
 */
export function selectCustomRowsCost(state: AnketaState): number {
  return state.customRows
    .filter((row) => row.enabled !== false)
    .reduce((sum, row) => sum + row.cost, 0);
}
